import { useCallback, useEffect, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { Dialog } from "primereact/dialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";
import { passengerAdmin, getAdminConfigWarning, readBases } from "../components/services/adminBackendFetch";
import { AdminPageHeader } from "../components/AdminPageHeader";
import { AdminTableShell } from "../components/AdminTableShell";
import { formatAdminDateTime } from "../utils/formatAdminDateTime";
import { useAppContext } from "../providers/ProviderAppContainer";

function money(n) {
    if (n == null || n === "") return "—";
    const v = Number(n);
    if (!Number.isFinite(v)) return "—";
    return v.toLocaleString("en-CA", { style: "currency", currency: "CAD" });
}

const STATUS_ALL = "all";

const STATUS_OPTIONS = [
    { label: "All statuses", value: STATUS_ALL },
    { label: "Completed", value: "completed" },
    { label: "Cancelled", value: "cancelled" },
];

function isAllStatus(value) {
    return value == null || value === "" || value === STATUS_ALL;
}

function toDateOnly(d) {
    if (!d) return "";
    const dt = d instanceof Date ? d : new Date(d);
    if (Number.isNaN(dt.getTime())) return "";
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const day = String(dt.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}

function downloadBlob(filename, blob) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}

function exportRowsToCsv(trips) {
    const headers = [
        "receipt_number",
        "status",
        "customer_name",
        "driver_name",
        "driver_car_number",
        "total_charged_pre_tax",
        "taxes",
        "other_fees",
        "tip",
        "total_charged",
        "total_driver_earning",
        "created_at",
    ];
    const esc = (v) => {
        if (v == null) return "";
        const s = String(v);
        if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
        return s;
    };
    const lines = [headers.join(",")];
    for (const t of trips) {
        lines.push(headers.map((h) => esc(t[h])).join(","));
    }
    downloadBlob("swyft-trip-reports.csv", new Blob([`${lines.join("\n")}\n`], { type: "text/csv;charset=utf-8" }));
}

function exportRowsToExcel(trips) {
    const headers = [
        "Receipt #",
        "Status",
        "Customer",
        "Driver",
        "Driver car #",
        "Pre-tax",
        "Taxes",
        "Other fees",
        "Tip",
        "Total charged",
        "Driver earning",
        "Created",
    ];
    const cell = (v) => `<td>${String(v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;")}</td>`;
    const rowsXml = trips
        .map(
            (t) =>
                `<tr>${[
                    t.receipt_number,
                    t.status,
                    t.customer_name,
                    t.driver_name,
                    t.driver_car_number,
                    t.total_charged_pre_tax,
                    t.taxes,
                    t.other_fees,
                    t.tip,
                    t.total_charged,
                    t.total_driver_earning,
                    t.created_at,
                ]
                    .map(cell)
                    .join("")}</tr>`,
        )
        .join("");
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8" /></head><body>
      <table border="1"><thead><tr>${headers.map((h) => `<th>${h}</th>`).join("")}</tr></thead>
      <tbody>${rowsXml}</tbody></table></body></html>`;
    downloadBlob(
        "swyft-trip-reports.xls",
        new Blob([html], { type: "application/vnd.ms-excel;charset=utf-8" }),
    );
}

function exportRowsToPdf(trips, filters) {
    const win = window.open("", "_blank", "noopener,noreferrer,width=960,height=720");
    if (!win) {
        throw new Error("Pop-up blocked — allow pop-ups to download PDF.");
    }
    const range =
        filters.from || filters.to
            ? `Date range: ${filters.from || "…"} → ${filters.to || "…"}`
            : "Date range: all";
    const rows = trips
        .map(
            (t) => `<tr>
        <td>${t.receipt_number || ""}</td>
        <td>${t.status || ""}</td>
        <td>${t.customer_name || ""}</td>
        <td>${t.driver_name || ""}</td>
        <td>${t.driver_car_number || ""}</td>
        <td>${t.total_charged_pre_tax ?? ""}</td>
        <td>${t.taxes ?? ""}</td>
        <td>${t.total_driver_earning ?? ""}</td>
        <td>${t.created_at || ""}</td>
      </tr>`,
        )
        .join("");
    win.document.write(`<!DOCTYPE html><html><head><title>SWYFT Trip Reports</title>
      <style>
        body{font:12px/1.4 system-ui,sans-serif;color:#111;padding:24px}
        h1{font-size:18px;margin:0 0 4px}
        .meta{color:#555;margin-bottom:16px}
        table{border-collapse:collapse;width:100%}
        th,td{border:1px solid #ccc;padding:6px 8px;text-align:left}
        th{background:#f3f4f6}
        @media print{button{display:none}}
      </style></head><body>
      <h1>SWYFT Trip Reports</h1>
      <div class="meta">${range} · ${trips.length} row(s) · exported ${new Date().toLocaleString()}</div>
      <button onclick="window.print()">Print / Save as PDF</button>
      <table><thead><tr>
        <th>Receipt #</th><th>Status</th><th>Customer</th><th>Driver</th><th>Car #</th>
        <th>Pre-tax</th><th>Taxes</th><th>Driver earning</th><th>Created</th>
      </tr></thead><tbody>${rows || `<tr><td colspan="9">No rows</td></tr>`}</tbody></table>
      <script>window.onload=function(){setTimeout(function(){window.print()},250)}</script>
      </body></html>`);
    win.document.close();
}

function ReceiptLines({ title, lines, totalLabel, total }) {
    if (!lines) return <div className="text-600 text-sm">No receipt breakdown available.</div>;
    return (
        <div className="mb-3">
            <div className="font-semibold text-900 mb-2">{title}</div>
            <div className="flex flex-column gap-1 text-sm">
                {Object.entries(lines).map(([k, v]) => (
                    <div key={k} className="flex justify-content-between gap-3">
                        <span className="text-600">{k.replace(/_/g, " ")}</span>
                        <span className="font-medium">{money(v)}</span>
                    </div>
                ))}
                <div className="flex justify-content-between gap-3 border-top-1 surface-border pt-2 mt-1 font-semibold">
                    <span>{totalLabel}</span>
                    <span>{money(total)}</span>
                </div>
            </div>
        </div>
    );
}

const emptyFilters = () => ({
    receipt_number: "",
    customer_name: "",
    driver_name: "",
    driver_car: "",
    status: STATUS_ALL,
    from: null,
    to: null,
});

export default function TripReportsPage() {
    const { showToast } = useAppContext();
    const configWarning = getAdminConfigWarning();
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [error, setError] = useState(null);
    const [rows, setRows] = useState([]);
    const [page, setPage] = useState(1);
    const [limit] = useState(50);
    const [total, setTotal] = useState(0);
    const [filters, setFilters] = useState(emptyFilters);
    const [receiptOpen, setReceiptOpen] = useState(false);
    const [selected, setSelected] = useState(null);

    const buildQuery = useCallback(
        (extra = {}) => {
            const qs = new URLSearchParams({
                page: String(extra.page ?? page),
                limit: String(extra.limit ?? limit),
            });
            for (const [k, v] of Object.entries(filters)) {
                if (k === "from" || k === "to") {
                    const day = toDateOnly(v);
                    if (day) qs.set(k, day);
                    continue;
                }
                if (k === "status") {
                    if (!isAllStatus(v)) qs.set(k, String(v).trim());
                    continue;
                }
                if (v != null && String(v).trim() !== "") qs.set(k, String(v).trim());
            }
            return qs;
        },
        [filters, limit, page],
    );

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await passengerAdmin(`/admin/reports/trips?${buildQuery().toString()}`);
            setRows(Array.isArray(data?.trips) ? data.trips : []);
            setTotal(Number(data?.total) || 0);
        } catch (e) {
            setError(e?.message || String(e));
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [buildQuery]);

    useEffect(() => {
        if (configWarning) {
            setLoading(false);
            return;
        }
        load();
    }, [configWarning, load]);

    async function fetchExportTrips() {
        const qs = buildQuery({ page: 1, limit: 5000 });
        qs.delete("page");
        qs.set("limit", "5000");
        qs.set("format", "json");
        const data = await passengerAdmin(`/admin/reports/trips/export?${qs.toString()}`);
        return Array.isArray(data?.trips) ? data.trips : [];
    }

    async function handleExport(kind) {
        setExporting(true);
        setError(null);
        try {
            const trips = await fetchExportTrips();
            const range = {
                from: toDateOnly(filters.from) || null,
                to: toDateOnly(filters.to) || null,
            };
            if (kind === "csv") {
                // Prefer server CSV when no need for client formatting.
                try {
                    const { passengerBase } = readBases();
                    const qs = buildQuery({ page: 1, limit: 5000 });
                    qs.delete("page");
                    qs.set("format", "csv");
                    qs.set("limit", "5000");
                    const url = `${passengerBase}/admin/reports/trips/export?${qs.toString()}`;
                    const res = await fetch(url);
                    if (!res.ok) throw new Error(`Export failed (${res.status})`);
                    const blob = await res.blob();
                    downloadBlob("swyft-trip-reports.csv", blob);
                } catch {
                    exportRowsToCsv(trips);
                }
            } else if (kind === "excel") {
                exportRowsToExcel(trips);
            } else if (kind === "pdf") {
                exportRowsToPdf(trips, range);
            }
            showToast?.({
                severity: "success",
                summary: "Export ready",
                detail: `${trips.length} trip(s) exported as ${kind.toUpperCase()}.`,
            });
        } catch (e) {
            setError(e?.message || String(e));
        } finally {
            setExporting(false);
        }
    }

    if (configWarning) {
        return <Message severity="warn" text={configWarning} className="w-full" />;
    }

    return (
        <div>
            <AdminPageHeader
                title="Trip reports"
                subtitle="Filter by date range and details. Export PDF, Excel, or CSV for the current filters."
            />
            {error ? <Message severity="error" text={error} className="w-full mb-3" /> : null}

            <div className="surface-card border-round-xl p-3 mb-3">
                <div className="grid">
                    <div className="col-12 md:col-3">
                        <label className="block text-sm font-medium mb-2">From date</label>
                        <Calendar
                            className="w-full"
                            value={filters.from}
                            onChange={(e) => setFilters((f) => ({ ...f, from: e.value }))}
                            dateFormat="yy-mm-dd"
                            showIcon
                            placeholder="Start date"
                        />
                    </div>
                    <div className="col-12 md:col-3">
                        <label className="block text-sm font-medium mb-2">To date</label>
                        <Calendar
                            className="w-full"
                            value={filters.to}
                            onChange={(e) => setFilters((f) => ({ ...f, to: e.value }))}
                            dateFormat="yy-mm-dd"
                            showIcon
                            placeholder="End date"
                        />
                    </div>
                    <div className="col-12 md:col-3">
                        <label className="block text-sm font-medium mb-2">Receipt #</label>
                        <InputText
                            className="w-full"
                            value={filters.receipt_number}
                            onChange={(e) => setFilters((f) => ({ ...f, receipt_number: e.target.value }))}
                            placeholder="R000001"
                        />
                    </div>
                    <div className="col-12 md:col-3">
                        <label className="block text-sm font-medium mb-2">Status</label>
                        <Dropdown
                            className="w-full"
                            value={filters.status ?? STATUS_ALL}
                            options={STATUS_OPTIONS}
                            optionLabel="label"
                            optionValue="value"
                            onChange={(e) => {
                                setFilters((f) => ({ ...f, status: e.value ?? STATUS_ALL }));
                                setPage(1);
                            }}
                        />
                    </div>
                    <div className="col-12 md:col-3">
                        <label className="block text-sm font-medium mb-2">Customer name</label>
                        <InputText
                            className="w-full"
                            value={filters.customer_name}
                            onChange={(e) => setFilters((f) => ({ ...f, customer_name: e.target.value }))}
                        />
                    </div>
                    <div className="col-12 md:col-3">
                        <label className="block text-sm font-medium mb-2">Driver name</label>
                        <InputText
                            className="w-full"
                            value={filters.driver_name}
                            onChange={(e) => setFilters((f) => ({ ...f, driver_name: e.target.value }))}
                        />
                    </div>
                    <div className="col-12 md:col-3">
                        <label className="block text-sm font-medium mb-2">Driver car #</label>
                        <InputText
                            className="w-full"
                            value={filters.driver_car}
                            onChange={(e) => setFilters((f) => ({ ...f, driver_car: e.target.value }))}
                        />
                    </div>
                    <div className="col-12 md:col-3 flex align-items-end gap-2 flex-wrap">
                        <Button
                            label="Apply filters"
                            icon="pi pi-filter"
                            onClick={() => {
                                setPage(1);
                                load();
                            }}
                        />
                        <Button
                            label="Reset"
                            icon="pi pi-times"
                            outlined
                            onClick={() => {
                                setFilters(emptyFilters());
                                setPage(1);
                            }}
                        />
                    </div>
                    <div className="col-12 flex flex-wrap gap-2 mt-2">
                        <Button
                            label="Export CSV"
                            icon="pi pi-file"
                            outlined
                            loading={exporting}
                            onClick={() => handleExport("csv")}
                        />
                        <Button
                            label="Export Excel"
                            icon="pi pi-file-excel"
                            outlined
                            loading={exporting}
                            onClick={() => handleExport("excel")}
                        />
                        <Button
                            label="Export PDF"
                            icon="pi pi-file-pdf"
                            outlined
                            loading={exporting}
                            onClick={() => handleExport("pdf")}
                        />
                    </div>
                </div>
            </div>

            <AdminTableShell header={<span className="font-semibold">Trips ({total})</span>}>
                {loading ? (
                    <div className="flex justify-content-center py-5">
                        <ProgressSpinner />
                    </div>
                ) : (
                    <DataTable
                        value={rows}
                        size="small"
                        stripedRows
                        paginator
                        rows={limit}
                        first={(page - 1) * limit}
                        totalRecords={total}
                        lazy
                        onPage={(e) => {
                            const nextPage = Math.floor(e.first / e.rows) + 1;
                            setPage(nextPage);
                        }}
                        emptyMessage="No trips match these filters."
                    >
                        <Column
                            header="Receipt #"
                            body={(row) =>
                                row.receipt_number ? (
                                    <button
                                        type="button"
                                        className="uc-admin-link-btn"
                                        onClick={() => {
                                            setSelected(row);
                                            setReceiptOpen(true);
                                        }}
                                    >
                                        {row.receipt_number}
                                    </button>
                                ) : (
                                    "—"
                                )
                            }
                        />
                        <Column field="customer_name" header="Customer" body={(r) => r.customer_name || "—"} />
                        <Column
                            field="driver_car_number"
                            header="Driver car #"
                            body={(r) => r.driver_car_number || "—"}
                        />
                        <Column field="driver_name" header="Driver" body={(r) => r.driver_name || "—"} />
                        <Column header="Pre-tax" body={(r) => money(r.total_charged_pre_tax)} />
                        <Column header="Taxes" body={(r) => money(r.taxes)} />
                        <Column header="Other fees" body={(r) => money(r.other_fees)} />
                        <Column header="Driver earning" body={(r) => money(r.total_driver_earning)} />
                        <Column
                            header="Status"
                            body={(r) => (
                                <Tag
                                    value={r.status}
                                    severity={r.status === "completed" ? "success" : "warning"}
                                />
                            )}
                        />
                        <Column header="Created" body={(r) => formatAdminDateTime(r.created_at)} />
                    </DataTable>
                )}
            </AdminTableShell>

            <Dialog
                header={selected?.receipt_number ? `Receipt ${selected.receipt_number}` : "Receipt"}
                visible={receiptOpen}
                onHide={() => setReceiptOpen(false)}
                style={{ width: "min(36rem, 96vw)" }}
                className="uc-admin-dialog"
                draggable={false}
            >
                {selected ? (
                    <div>
                        <div className="text-sm text-600 mb-3">
                            {selected.customer_name || "Customer"} · Driver {selected.driver_name || "—"} ·{" "}
                            {selected.driver_car_number || "no plate"}
                        </div>
                        {selected.cancellation_receipt ? (
                            <div className="mb-3 surface-100 border-round p-3 text-sm">
                                <div className="font-semibold mb-1">Cancellation receipt</div>
                                <div>{selected.cancellation_receipt.message || selected.cancellation_receipt.title}</div>
                                <div className="mt-1">
                                    Fee: {money(selected.cancellation_receipt.fee ?? selected.cancellation_receipt.amount_charged)}
                                </div>
                            </div>
                        ) : null}
                        <ReceiptLines
                            title="Customer receipt"
                            lines={selected.customer_receipt?.line_items}
                            totalLabel="Total charge"
                            total={selected.customer_receipt?.total_charge}
                        />
                        <ReceiptLines
                            title="Driver receipt"
                            lines={
                                selected.driver_receipt
                                    ? {
                                          trip_fare: selected.driver_receipt.trip_fare,
                                          tip_earning: selected.driver_receipt.tip_earning,
                                      }
                                    : null
                            }
                            totalLabel="Total earning"
                            total={selected.driver_receipt?.total_earning}
                        />
                    </div>
                ) : null}
            </Dialog>
        </div>
    );
}
